import { useCallback, useEffect, useState } from 'react';

import { differenceBy, pick } from 'lodash-es';
import { useNavigate } from 'react-router-dom';

import { Nullable } from '@rbi-ctg/frontend';
import { ICartEntry, IItem } from '@rbi-ctg/menu';
import {
  IAddOnSection,
  IAddOnSectionOption,
  IItemsAcceptingFreeOffers,
} from 'hooks/use-feature-menu-add-on';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMenuOptionsContext } from 'state/menu-options';
import { useOrderContext } from 'state/order';
import { routes } from 'utils/routing';
import {
  convertModActionsToSelections,
  getSelectionsFromMenuData,
  transformMenuObjectToCartItem,
} from 'utils/wizard';

export type AddOnSelections = Record<
  string,
  { quantity: number; data: IItem; sectionId: string; cartId?: string }
>;
export type { IAddOnSection, IAddOnSectionOption };

interface IUseCartAddOnsOptions {
  initialSelections?: AddOnSelections;
  addOnData?: IAddOnSection[];
}

const useCartAddOns = (options?: IUseCartAddOnsOptions) => {
  const { initialSelections = {}, addOnData = [] } = options ?? {};
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [addOnSelections, setAddOnSelections] = useState<AddOnSelections>(initialSelections);
  const [previousSelections, setPreviousSelections] = useState<AddOnSelections>({});
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [totalQuantity, setTotalQuantity] = useState<number>(0);
  const navigate = useNavigate();
  const { priceForItemOptionModifier, priceForComboSlotSelection, pricingFunction } =
    useMenuOptionsContext();
  const enableFreeAddonOnCartFeatureFlag = useFlag(LaunchDarklyFlag.ENABLE_FREE_ADDON_ON_CART);

  const { upsertCart, cartEntries, removeAllFromCart, addToCart } = useOrderContext();

  // NOTE: Currently this only handles adding IItem menu item type as that is all addOns currently support in Sanity
  const handleAddToCart = () => {
    // just navigate to the cart nothing was selected. Treat this as if they exited the modal
    if (totalQuantity === 0) {
      navigate(routes.cart);
    }

    // We have to figure out which AddOns were there but no longer have quantity
    const entryKeysToRemove = differenceBy(
      Object.keys(previousSelections),
      Object.keys(addOnSelections)
    );

    const entriesToRemove = entryKeysToRemove.map(key => ({
      cartId: previousSelections[key].cartId,
    }));

    const newCartEntries: ICartEntry[] = Object.keys(addOnSelections).reduce(
      (entries: ICartEntry[], selectionKey) => {
        const { quantity, data, cartId: existingCartId } = addOnSelections[selectionKey];
        // Make sure to always get the default modifiers for the item
        const { modifierSelections: modSelectionActions } = getSelectionsFromMenuData({ data });
        // Convert the returned action into wizard state
        const modifierSelections = modSelectionActions
          ? convertModActionsToSelections(modSelectionActions)
          : [];

        // Make sure to always include modifiers that are injected by default by passing it through the same function wizard uses
        const { mainEntry } = transformMenuObjectToCartItem(
          data,
          priceForItemOptionModifier,
          pricingFunction,
          priceForComboSlotSelection,
          { quantity, modifierSelections, existingCartId }
        );
        if (mainEntry) {
          entries.push({
            ...mainEntry,
            isExtra: true,
          });
        }
        return entries;
      },
      []
    );

    removeAllFromCart(entriesToRemove);
    upsertCart(newCartEntries);
    navigate(routes.cart);
  };

  const handleAddItemToCart = useCallback(
    (updatedItem: IItem, quantity = 1) => {
      // Make sure to always get the default modifiers for the item
      const { modifierSelections: modSelectionActions } = getSelectionsFromMenuData({
        data: updatedItem,
      });
      // Convert the returned action into wizard state
      const modifierSelections = modSelectionActions
        ? convertModActionsToSelections(modSelectionActions)
        : [];

      // Make sure to always include modifiers that are injected by default by passing it through the same function wizard uses
      const { mainEntry } = transformMenuObjectToCartItem(
        updatedItem,
        priceForItemOptionModifier,
        pricingFunction,
        priceForComboSlotSelection,
        { quantity, modifierSelections }
      );

      addToCart({ ...mainEntry, isExtra: true });
    },
    [addToCart, priceForComboSlotSelection, priceForItemOptionModifier, pricingFunction]
  );

  // If the new quantity is zero remove that options itemId from the selections completely
  // Otherwise update that key to the new quantity/data
  const updateQuantityForItem = (
    section: IAddOnSection,
    sectionOption: IAddOnSectionOption,
    option: IItem,
    quantity: number,
    keyId: string,
    isFreeAddon: boolean,
    maxQtdFree: number
  ) => {
    const optionId = sectionOption._key;
    const data = option;
    const sectionId = section._id;

    setAddOnSelections(currentSelections => {
      const isNotMaxFreeQtd = isFreeAddon && quantity > 0 && quantity < maxQtdFree;

      const updatedItem = {
        [keyId]: {
          quantity,
          data,
          sectionId,
          cartId: currentSelections[keyId]?.cartId,
        },
      };

      const selectionsToKeep = Object.keys(currentSelections).filter(key => {
        const isFreeAddonKey = key !== optionId;
        const isKeyNotFound = key !== keyId && key !== optionId;

        return isNotMaxFreeQtd ? isFreeAddonKey : isKeyNotFound;
      });

      const removeItems = () => {
        const selectionsToPick = pick(currentSelections, selectionsToKeep);

        const returnData = isNotMaxFreeQtd
          ? {
              ...selectionsToPick,
              ...updatedItem,
            }
          : selectionsToPick;

        return returnData;
      };

      return quantity === 0 || isNotMaxFreeQtd
        ? removeItems()
        : { ...currentSelections, ...updatedItem };
    });
  };

  // Recalculate the total anytime the selections change.
  useEffect(() => {
    const { calculatedPrice: newPrice, calculatedQuantity: newQuantity } = Object.keys(
      addOnSelections
    ).reduce(
      ({ calculatedPrice, calculatedQuantity }, selectionKey) => {
        const { quantity, data } = addOnSelections[selectionKey];
        return {
          calculatedPrice: calculatedPrice + pricingFunction(data, quantity),
          calculatedQuantity: calculatedQuantity + quantity,
        };
      },
      { calculatedPrice: 0, calculatedQuantity: 0 }
    );
    setTotalPrice(newPrice);
    setTotalQuantity(newQuantity);
  }, [addOnSelections, pricingFunction]);

  const addOnHasOffer = useCallback(
    (cartEntry: ICartEntry[], itemsAcceptingFreeOffers: Nullable<IItemsAcceptingFreeOffers[]>) => {
      const isInEntries = (entry: ICartEntry) =>
        itemsAcceptingFreeOffers?.some(
          ({ itemAcceptingFreeOffer }) => entry._id === itemAcceptingFreeOffer._id
        );

      return cartEntry.some(isInEntries);
    },
    []
  );

  // Preload selections when we mount.
  useEffect(() => {
    // if no addOnData or cartEntries don't do anything
    if (!addOnData.length || !cartEntries.length) {
      return;
    }

    // get an object of all extra entries with keys being the items id
    const cartEntrySelections = cartEntries.reduce(
      (selections: Record<string, ICartEntry>, entry: ICartEntry) => {
        if (entry.isExtra) {
          return { ...selections, [entry._id]: entry };
        }
        return selections;
      },
      {}
    );

    if (!Object.keys(cartEntrySelections).length) {
      return;
    }

    setIsEdit(true);

    const preloadedSelections = addOnData.reduce((selections: AddOnSelections, section) => {
      let sectionSelections = {};
      section.options.forEach(
        ({
          _key: selectionKey,
          option: optionItem,
          freeOption: freeItem,
          itemsAcceptingFreeOffers,
        }) => {
          const isAddonFreeEnabled =
            enableFreeAddonOnCartFeatureFlag && section.enableAddonAsFreeItem;

          if (freeItem && !addOnHasOffer(cartEntries, itemsAcceptingFreeOffers)) {
            return;
          }

          const createSectionSelections = (entry: ICartEntry, item: IItem) => ({
            quantity: entry.quantity,
            data: item,
            sectionId: section._id,
            cartId: entry.cartId,
          });

          const cartEntryOptionSelection = cartEntrySelections[optionItem._id];
          const cartEntryFreeOptionSelection = freeItem && cartEntrySelections[freeItem._id];

          if (optionItem && cartEntryOptionSelection) {
            sectionSelections = {
              ...sectionSelections,
              [selectionKey]: createSectionSelections(cartEntryOptionSelection, optionItem),
            };
          }

          if (isAddonFreeEnabled && freeItem && cartEntryFreeOptionSelection) {
            sectionSelections = {
              ...sectionSelections,
              [freeItem._id]: createSectionSelections(cartEntryFreeOptionSelection, freeItem),
            };
          }
        }
      );

      return { ...selections, ...sectionSelections };
    }, {});

    setPreviousSelections(preloadedSelections); // hold onto the selections we load with before the guest makes changes
    setAddOnSelections(preloadedSelections);
  }, [addOnData, addOnHasOffer, cartEntries, enableFreeAddonOnCartFeatureFlag]);

  return {
    handleAddToCart,
    isEdit,
    updateQuantityForItem,
    addOnSelections,
    totalPrice,
    totalQuantity,
    handleAddItemToCart,
  };
};

export default useCartAddOns;
